import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle, Bug, FileText, ClipboardCheck, Wrench,
  ArrowRight, Sparkles, Clock, AlertCircle, Target, CheckCircle2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

const TYPE_CONFIG = {
  risk:               { icon: AlertTriangle,   labelEn: "Risk",            labelEs: "Riesgo" },
  corrective_action:  { icon: Wrench,          labelEn: "Corrective Action", labelEs: "Acción correctiva" },
  audit:              { icon: ClipboardCheck,  labelEn: "Internal Audit",  labelEs: "Auditoría interna" },
  document_review:    { icon: FileText,        labelEn: "Document review", labelEs: "Revisión de documento" },
  incident:           { icon: Bug,             labelEn: "Incident",        labelEs: "Incidente" },
};

const SEVERITY_TONE = {
  critical: "text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/20 border-red-200 dark:border-red-800",
  high:     "text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800",
  medium:   "text-yellow-700 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800",
  low:      "text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/20 border-green-200 dark:border-green-800",
  info:     "text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800",
};

function relativeTime(iso, isES) {
  if (!iso) return "";
  try {
    const date = new Date(iso);
    const diffMs = date.getTime() - Date.now();
    const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
    if (days < 0) {
      const abs = Math.abs(days);
      return isES ? `hace ${abs} ${abs === 1 ? "día" : "días"}` : `${abs} day${abs === 1 ? "" : "s"} ago`;
    }
    if (days === 0) return isES ? "hoy" : "today";
    if (days === 1) return isES ? "mañana" : "tomorrow";
    return isES ? `en ${days} días` : `in ${days} days`;
  } catch { return ""; }
}

export function NextActions({ items = [], loading = false, isES = false, className }) {
  const navigate = useNavigate();

  return (
    <Card className={cn("border-border", className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" aria-hidden="true" />
          {isES ? "Tus próximos pasos" : "Your next moves"}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 space-y-3" role="status" aria-busy="true" aria-label="Loading">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-8 h-8 rounded-full bg-muted" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-muted rounded w-2/3" />
                  <div className="h-2 bg-muted rounded w-1/3" />
                </div>
              </div>
            ))}
            <span className="sr-only">Loading next actions</span>
          </div>
        ) : items.length === 0 ? (
          <div className="px-6 py-10 text-center flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-primary" aria-hidden="true" />
            </div>
            <div className="space-y-1">
              <p className="text-base font-semibold">
                {isES ? "¡Todo al día!" : "All caught up!"}
              </p>
              <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                {isES
                  ? "No hay acciones pendientes. Aprovecha para revisar tus brechas y planear el siguiente sprint."
                  : "No pending actions. Use this moment to review your gaps and plan the next sprint."}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-1 gap-2 border-primary/40 text-primary hover:bg-primary/10 hover:text-primary"
              onClick={() => navigate("/gap-analysis")}
            >
              <Target className="w-3.5 h-3.5" aria-hidden="true" />
              {isES ? "Ir al Análisis de Brechas" : "Go to Gap Analysis"}
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-border" role="list">
            {items.map((item, idx) => {
              const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.risk;
              const Icon = cfg.icon;
              const tone = SEVERITY_TONE[item.severity] || SEVERITY_TONE.medium;
              const typeLabel = isES ? cfg.labelEs : cfg.labelEn;
              return (
                <motion.li
                  key={`${item.type}-${item.id || idx}`}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: idx * 0.03 }}
                >
                  <button
                    type="button"
                    onClick={() => navigate(item.path)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none transition-colors text-left"
                    aria-label={`${typeLabel}: ${item.title}`}
                  >
                    <div className={cn("flex-shrink-0 w-8 h-8 rounded-full border flex items-center justify-center", tone)}>
                      <Icon className="w-4 h-4" aria-hidden="true" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {typeLabel}
                        </span>
                        {item.overdue && (
                          <Badge variant="outline" className="bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800 text-[10px] px-1.5 py-0">
                            <AlertCircle className="w-2.5 h-2.5 mr-0.5" aria-hidden="true" />
                            {isES ? "Vencido" : "Overdue"}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      {item.due_date && (
                        <p className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-0.5">
                          <Clock className="w-3 h-3" aria-hidden="true" />
                          {relativeTime(item.due_date, isES)}
                        </p>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                  </button>
                </motion.li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
